/**
 * Sourcing (Sondar) — pure client helpers.
 *
 * Ported from the source tool's `lib/` modules (csv, summary, score-display,
 * semantic, signals). They are browser-safe and have no server dependencies:
 * formatting and CSV serialization over the `ScoredCandidate` shape from
 * `@shared/labs/sourcing`.
 */
import type {
  ExplorationView,
  FacetSelection,
  JudgedCandidate,
  ProjectStage,
  ScoredCandidate,
  ScoreSignal,
  SourcingFlowKind,
  SourcingProjectCandidateView,
  SourcingProjectView,
  StructuredQuery,
} from "@shared/labs/sourcing";

// ─── Shareable deep-link ──────────────────────────────────────────────────
// Any Labs user who opens this URL drops straight into the run's results (the
// read is owner-shared and free). `kind` selects the flow so the right chrome
// renders. Read back in `index.tsx#readDeepLink`.

export function explorationShareLink(id: string, kind: SourcingFlowKind): string {
  return `${window.location.origin}/labs/sourcing?exploration=${id}&kind=${kind}`;
}

// ─── Precise-search filter seeding ────────────────────────────────────────
// `understand` proposes a diverse SET of arms; the precise editor works on ONE
// consolidated filter set. Seed it from the first arm (the broad anchor) and
// backfill any empty structured facet from `query.filters`, so the recruiter
// starts from the richest correct guess and edits from there.

export function consolidateArm(query: StructuredQuery): FacetSelection {
  const base: Partial<FacetSelection> = query.arms?.[0] ?? {};
  const f = query.filters;
  return {
    label: base.label,
    title: (base.title || f.title || "").trim(),
    locale: base.locale,
    currentJobTitles: base.currentJobTitles,
    seniorityBand: base.seniorityBand ?? f.seniorityBand,
    excludeManagement: base.excludeManagement,
    functionIds: base.functionIds ?? (f.functionId ? [f.functionId] : undefined),
    industryIds: base.industryIds,
    minYears: base.minYears ?? f.minYearsExperience,
    companyHeadcount: base.companyHeadcount,
    useCompanies: base.useCompanies,
    usePastCompanies: base.usePastCompanies,
    useSchools: base.useSchools,
    recentlyChangedJobs: base.recentlyChangedJobs,
  };
}

/**
 * The effective search REGIONS shown to the recruiter — the countries each arm actually searches.
 * Mirrors the backend's location resolution (`resolveLocationBranches`) but stays client-only, since
 * comp's client cannot import server code: the LLM's already-canonicalized `locationBranches` win;
 * else a single named city; else the fixed Brazil safety net (a place-less brief always defaults to
 * Brazil). Always ≥1 branch, so the recruiter always sees the exact regions the search targets.
 */
export function effectiveSearchRegions(query: StructuredQuery): string[][] {
  const branches = query.filters.locationBranches;
  if (branches?.length) return branches;
  const city = query.filters.location?.trim();
  if (city) return [[city]];
  return [["Brasil"]];
}

// ─── Semantic strength ────────────────────────────────────────────────────
// Maps a 0–1 cosine into a coarse 1–5 band + label, used to render the
// "reads like the role" signal without exposing the raw number.

export interface SemanticStrength {
  bars: number;
  label: string;
}

export function semanticStrength(cosine: number): SemanticStrength {
  const c = Number.isFinite(cosine) ? cosine : 0;
  if (c < 0.08) return { bars: 1, label: "Fraco" };
  if (c < 0.16) return { bars: 2, label: "Razoável" };
  if (c < 0.27) return { bars: 3, label: "Moderado" };
  if (c < 0.37) return { bars: 4, label: "Forte" };
  return { bars: 5, label: "Excelente" };
}

// ─── Signal labelling ─────────────────────────────────────────────────────

export function signalLabel(s: ScoreSignal): string {
  if (s.key === "semantic") {
    const cosine = s.detail ? Number.parseFloat(s.detail) : 0;
    return `Lê como a vaga: ${semanticStrength(cosine).label}`;
  }
  return s.label;
}

// ─── Tenure / duration formatting ─────────────────────────────────────────
// Render a month count as a pt-BR "X anos Y meses" label — used for the card's
// current-role line and each work-history entry. Returns null when there's
// nothing to show (missing, non-finite, or rounding to zero) so the caller can
// omit the label entirely rather than print an empty or "0 meses" string.

export function formatTenure(months?: number | null): string | null {
  if (months == null || !Number.isFinite(months) || months <= 0) return null;
  const total = Math.round(months);
  if (total === 0) return null;
  const years = Math.floor(total / 12);
  const rem = total % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "ano" : "anos"}`);
  if (rem > 0) parts.push(`${rem} ${rem === 1 ? "mês" : "meses"}`);
  return parts.join(" ");
}

// ─── Location formatting ──────────────────────────────────────────────────
// LinkedIn locations are verbose and often redundant — "Rio de Janeiro, Rio de
// Janeiro, Brazil", "Greater São Paulo Area". In dense list contexts (the triage
// row) the full string competes with the candidate's name for width, so we show
// just the primary locality (the first comma-delimited segment) and keep the
// full string available via `title` for anyone who needs the region/country.

export function shortLocation(location?: string | null): string | null {
  const primary = location?.split(",")[0]?.trim();
  return primary && primary.length > 0 ? primary : null;
}

// ─── Triage-board search + collision resolution ──────────────────────────────
// Pure helpers backing the project triage board: filtering the visible list by a
// free-text query, and resolving a set of "already in the project" candidate ids
// (returned by an add call) back to their project rows so the collision board can
// place them at their current stage.

/** The text a candidate is matched against in the triage search box: name,
 *  current title, primary location, and current employer. */
export function candidateSearchText(c: ScoredCandidate): string {
  return [c.name, c.currentTitle, c.location, c.experience?.[0]?.company]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** Narrow a stage's rows to those matching every whitespace-split term of the
 *  query (substring, case-insensitive). Empty/blank query → all rows. */
export function filterProjectRows(
  rows: SourcingProjectCandidateView[],
  query: string,
): SourcingProjectCandidateView[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return rows;
  return rows.filter((r) => {
    const hay = candidateSearchText(r.candidate);
    return terms.every((t) => hay.includes(t));
  });
}

/** Map the `skippedCandidateIds` an add call reports (candidates already in the
 *  project) back to their current project rows, dropping any not found in the
 *  loaded view. Match is case-insensitive on the LinkedIn id. */
export function resolveCollisions(
  skippedCandidateIds: string[],
  candidates: SourcingProjectCandidateView[],
): SourcingProjectCandidateView[] {
  const wanted = new Set(skippedCandidateIds.map((id) => id.toLowerCase()));
  return candidates.filter((c) => wanted.has(c.candidateId.toLowerCase()));
}

// ─── Deep JSON export ───────────────────────────────────────────────────────
// The shortlist export is a structured JSON dump (not a flat CSV) so a recruiter
// gets the WHOLE candidate, not just the five headline columns: the basics, the
// full normalized LinkedIn profile, both chip sets (positive signals + the judge's
// gaps), and the complete LLM verdict with its sub-judgements. It's free — built
// entirely from the already-loaded result set, no extra search/judge spend.

/** One candidate's full export record — everything the UI knows about them. */
export interface CandidateExport {
  /** 1-based position in the FIT-ranked shortlist (matches the card headline). */
  rank: number;
  basics: {
    name: string;
    url: string;
    location: string | null;
    headline: string | null;
    /** Current role + tenure — the recruiter's "what they do now". */
    currentTitle: string | null;
    currentTenureMonths: number | null;
  };
  /** The normalized LinkedIn profile (the candidate object as surfaced by the
   *  search actor and mapped to our `Profile` shape — about, full history, skills). */
  linkedinProfile: {
    id: string;
    headline: string | null;
    about: string | null;
    currentTitle: string | null;
    currentTenureMonths: number | null;
    location: string | null;
    experience: JudgedCandidate["experience"];
    skills: string[];
    education: JudgedCandidate["education"];
  };
  chips: {
    /** Positive "why this matched" signals (deterministic found-keyword/employer/school
     *  + the humanized semantic chip). Excludes keyword-absence `gap:*` signals. */
    strengths: { key: string; label: string; contribution: number; detail?: string }[];
    /** Negative "gaps to validate" — judge-authored, intent-read (empty on legacy rows). */
    gaps: string[];
  };
  /** The LLM judge's verdict: headline FIT plus the five anchored sub-judgements. */
  evaluation: {
    /** FIT in [0,1] — the sort/display key. */
    fitScore: number;
    /** FIT as 0–100, matching the card headline. */
    fitPct: number;
    /** Bandit reward (evidence core, no pedigree bonus) — absent on legacy rows. */
    reward?: number;
    reasoning: string;
    subScores: {
      /** -2 far below · -1 below · 0 on-target · +1 above · +2 far above. */
      levelFit?: number;
      /** must-haves: 0 unlikely · 1 plausible · 2 likely. */
      mustHave?: number;
      /** nice-to-haves: 0 · 1 · 2. */
      niceHave?: number;
      /** top-tier university: 0 no · 1 yes. */
      eduPedigree?: number;
      /** top-tier employers: 0 no · 1 yes. */
      workPedigree?: number;
      /** profile legibility: 0 thin · 1 medium · 2 rich. */
      evidence?: number;
    };
  } | null;
  /** Deterministic ranking signal behind the sort (separate from the LLM verdict). */
  ranking: {
    /** 0–100 composite (deterministic signals + semantic cosine). */
    compositeScore: number;
    qualified: boolean;
    passedHardGate: boolean;
    /** Which query variant(s) surfaced this profile (search provenance). */
    foundByVariants: string[];
  };
}

/** The whole export payload — a small run header + one record per candidate. */
export interface ShortlistExport {
  exploration: {
    id: string;
    brief: string;
    status: ExplorationView["status"];
    totalCandidates: number;
    pagesUsed: number;
  };
  candidates: CandidateExport[];
}

function candidateExport(c: JudgedCandidate, rank: number): CandidateExport {
  const j = c.judgement;
  return {
    rank,
    basics: {
      name: c.name,
      url: c.url,
      location: c.location ?? null,
      headline: c.headline ?? null,
      currentTitle: c.currentTitle ?? null,
      currentTenureMonths: c.currentTenureMonths ?? null,
    },
    linkedinProfile: {
      id: c.id,
      headline: c.headline ?? null,
      about: c.about ?? null,
      currentTitle: c.currentTitle ?? null,
      currentTenureMonths: c.currentTenureMonths ?? null,
      location: c.location ?? null,
      experience: c.experience,
      skills: c.skills,
      education: c.education,
    },
    chips: {
      strengths: c.signals
        .filter((s) => !s.key.startsWith("gap"))
        .map((s) => ({
          key: s.key,
          label: signalLabel(s),
          contribution: s.contribution,
          ...(s.detail !== undefined ? { detail: s.detail } : {}),
        })),
      gaps: j?.gaps ?? [],
    },
    evaluation: j
      ? {
          fitScore: j.score,
          fitPct: Math.round(j.score * 100),
          ...(j.reward !== undefined ? { reward: j.reward } : {}),
          reasoning: j.reasoning,
          subScores: {
            levelFit: j.levelFit,
            mustHave: j.mustHave,
            niceHave: j.niceHave,
            eduPedigree: j.eduPedigree,
            workPedigree: j.workPedigree,
            evidence: j.evidence,
          },
        }
      : null,
    ranking: {
      compositeScore: c.score,
      qualified: c.qualified,
      passedHardGate: c.passedHardGate,
      foundByVariants: c.foundByVariants,
    },
  };
}

/** Build the structured export payload from a finished/partial exploration view. */
export function toExportJson(view: ExplorationView): ShortlistExport {
  return {
    exploration: {
      id: view.id,
      brief: view.brief,
      status: view.status,
      totalCandidates: view.results.length,
      pagesUsed: view.pagesUsed,
    },
    candidates: view.results.map((c, i) => candidateExport(c, i + 1)),
  };
}

function triggerJsonDownload(payload: unknown, filename: string): void {
  const blob = new Blob([`${JSON.stringify(payload, null, 2)}\n`], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Trigger a browser download of the result set as a deep `candidatos.json`. */
export function downloadJson(view: ExplorationView): void {
  triggerJsonDownload(toExportJson(view), "candidatos.json");
}

// ─── Project export ─────────────────────────────────────────────────────────
// Same deep per-candidate record as the run export, plus the project's triage
// state (stage + entry path) — the whole tracked pool for the open position.

export interface ProjectExport {
  project: {
    id: string;
    name: string;
    brief: string;
    createdAt: string;
    totalCandidates: number;
    sessionCount: number;
    /** Present on a filtered export: which triage piles this file contains. */
    stages?: ProjectStage[];
  };
  candidates: (CandidateExport & {
    stage: ProjectStage;
    /** How the candidate entered: exploration | manual-url | manual-pick. */
    source: string;
    /** Which brief the verdict was judged against: project | session. */
    judgedAgainst: "project" | "session";
  })[];
}

/** Canonical pile order, used to normalize export filenames/headers. */
const STAGE_ORDER: ProjectStage[] = ["sourced", "shortlisted", "rejected"];

/**
 * Build the project export — READY candidates only (pending/error rows carry
 * no verdict), FIT-ranked, stage carried per record. Each candidate record is
 * the SAME `CandidateExport` shape as the search-flow export. Pass `stages` to
 * export a SELECTION of triage piles (rank is then the position within the
 * exported set); omitted/empty/all-three = the whole pool.
 */
export function toProjectExportJson(
  view: SourcingProjectView,
  stages?: ProjectStage[],
): ProjectExport {
  const wanted = stages?.length ? new Set(stages) : null;
  const isSubset = wanted !== null && wanted.size < STAGE_ORDER.length;
  const ready = view.candidates
    .filter((c) => c.fetchStatus === "ready" && (wanted === null || wanted.has(c.stage)))
    .sort((a, b) => (b.judgement?.score ?? 0) - (a.judgement?.score ?? 0));
  return {
    project: {
      id: view.id,
      name: view.name,
      brief: view.brief,
      createdAt: view.createdAt,
      totalCandidates: ready.length,
      sessionCount: view.sessions.length,
      ...(isSubset ? { stages: STAGE_ORDER.filter((s) => wanted.has(s)) } : {}),
    },
    candidates: ready.map((c, i) => ({
      ...candidateExport({ ...c.candidate, judgement: c.judgement }, i + 1),
      stage: c.stage,
      source: c.source,
      judgedAgainst: c.judgedAgainst,
    })),
  };
}

/** Trigger a browser download of the project pool — the whole pool (no/all
 *  stages) as `projeto-candidatos.json`, or a pile selection as
 *  `projeto-<stages>.json`. */
export function downloadProjectJson(view: SourcingProjectView, stages?: ProjectStage[]): void {
  const wanted = stages?.length ? new Set(stages) : null;
  const isSubset = wanted !== null && wanted.size < STAGE_ORDER.length;
  const filename = isSubset
    ? `projeto-${STAGE_ORDER.filter((s) => wanted.has(s)).join("-")}.json`
    : "projeto-candidatos.json";
  triggerJsonDownload(toProjectExportJson(view, stages), filename);
}
